import axios from 'axios';
/**
 * Creates a configured API service instance
 */
export function createApiService(config = {}) {
    const instance = axios.create({
        baseURL: config.baseURL || '',
        timeout: config.timeout || 10000,
        withCredentials: config.withCredentials ?? true,
        headers: {
            'Content-Type': 'application/json',
            ...config.headers,
        },
    });
    // Request interceptor
    instance.interceptors.request.use((config) => {
        // Add any request modifications here
        return config;
    }, (error) => {
        return Promise.reject(error);
    });
    // Response interceptor
    instance.interceptors.response.use((response) => {
        // Add any response modifications here
        return response;
    }, (error) => {
        // Handle common errors
        if (error.response?.status === 401) {
            // Handle unauthorized access
            console.warn('Unauthorized access - consider redirecting to login');
        }
        return Promise.reject(error);
    });
    return {
        get: (url, config) => instance.get(url, config),
        post: (url, data, config) => instance.post(url, data, config),
        put: (url, data, config) => instance.put(url, data, config),
        patch: (url, data, config) => instance.patch(url, data, config),
        delete: (url, config) => instance.delete(url, config),
    };
}
/**
 * Default API service instance
 */
export const defaultApiService = createApiService();
/**
 * Creates a medusa-compatible request function
 * This maintains compatibility with the existing admin-ui API structure
 */
export function createMedusaRequest(baseURL, defaultHeaders = {}) {
    const instance = axios.create({
        baseURL,
        withCredentials: true,
        headers: {
            'Content-Type': 'application/json',
            ...defaultHeaders,
        },
    });
    return function medusaRequest(method, path = '', payload = {}, abortController = new AbortController(), customHeaders = {}) {
        const options = {
            method: method.toLowerCase(),
            withCredentials: true,
            url: path,
            data: payload,
            signal: abortController.signal,
            headers: {
                ...customHeaders,
            },
        };
        return instance(options);
    };
}
/**
 * Utility function to build query parameters from an object
 */
export function buildQueryFromObject(search, prefix = '') {
    return Object.entries(search)
        .map(([key, value]) => {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            return buildQueryFromObject(value, key);
        }
        return `${prefix ? `${prefix}[${key}]` : `${key}`}=${encodeURIComponent(value)}`;
    })
        .join('&');
}
/**
 * Utility function to remove nullish values from an object
 */
export function removeNullish(obj) {
    return Object.entries(obj).reduce((acc, [key, value]) => {
        if (value != null) {
            acc[key] = value;
        }
        return acc;
    }, {});
}
/**
 * Utility function to build URL with query parameters
 */
export function buildUrlWithParams(baseUrl, params = {}) {
    const cleanParams = removeNullish(params);
    const queryString = buildQueryFromObject(cleanParams);
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}
//# sourceMappingURL=index.js.map